import type { LapRef, ResampledLap, SessionData } from '@tla/types';
import { deserializeSessionData, resampleLap, withDerivedChannels } from '@tla/core';
import { api } from '../api/client';

/** Number of points each lap is resampled onto for comparison/plotting. */
export const GRID_SIZE = 1000;

const sessionCache = new Map<string, Promise<SessionData>>();

/**
 * Load and cache the parsed {@link SessionData} for a session id. The telemetry
 * blob is fetched once, deserialised, and augmented with derived channels.
 */
export function loadSession(sessionId: string): Promise<SessionData> {
  let entry = sessionCache.get(sessionId);
  if (!entry) {
    entry = api
      .getSessionData(sessionId)
      .then((bytes) => withDerivedChannels(deserializeSessionData(bytes)));
    sessionCache.set(sessionId, entry);
  }
  return entry;
}

/** Drop a session from the cache (e.g. after deletion). */
export function evictSession(sessionId: string): void {
  sessionCache.delete(sessionId);
}

/** All channel ids present across a set of loaded sessions, order-preserved. */
export function channelUnion(sessions: SessionData[]): {
  id: string;
  name: string;
  unit: string;
  group: string;
}[] {
  const seen = new Map<string, { id: string; name: string; unit: string; group: string }>();
  for (const s of sessions) {
    for (const c of s.channels) {
      if (!seen.has(c.id)) {
        seen.set(c.id, { id: c.id, name: c.name, unit: c.unit, group: c.group });
      }
    }
  }
  return [...seen.values()];
}

/**
 * Resample a specific lap (by reference) onto the shared distance grid for the
 * requested channels. Returns null if the session/lap cannot be found.
 */
export async function resampleLapRef(
  ref: LapRef,
  channelIds: string[],
): Promise<ResampledLap | null> {
  const session = await loadSession(ref.sessionId);
  const lap = session.laps.find((l) => l.number === ref.lapNumber);
  if (!lap) return null;
  const resampled = resampleLap(session, lap, channelIds, GRID_SIZE);
  resampled.sessionId = ref.sessionId;
  resampled.lapNumber = ref.lapNumber;
  return resampled;
}
